<template>
    <form class="form"
        @submit.prevent
    >
        <header class="header">
            <h1>{{ $t('turkishExtraFields.pageTitle') }}</h1>
        </header>
        <div class="wrapper wrapper--no-max-width">
            <h2 class="section__title">{{ $t('turkishExtraFields.birthInformations') }}</h2>
            <section class="section">
                <custom-input v-model="cityOfBirth"
                    name="city-of-birth"
                    class="input--half"
                    :label="$t('turkishExtraFields.cityOfBirthLabel')"
                    :placeholder="$t('turkishExtraFields.cityOfBirthPlaceholder')"
                    required
                />

                <div class="label label--half label--bottom-padding">
                    {{ $t('turkishExtraFields.countryOfBirthLabel') }}

                    <country-dropdown :country.sync="countryOfBirth"
                        :placeholder="$t('turkishExtraFields.countryOfBirthPlaceholder')"
                        :noResultsMessage="$t('kycUserIdentity.noResultsCountries')"
                        required
                    />
                </div>
            </section>

            <h2 class="section__title">{{ $t('turkishExtraFields.addressInformations') }}</h2>
            <section class="section">
                <custom-input v-model="address.street"
                    name="street"
                    class="input--half"
                    :label="$t('profile.postalAddressPage.street')"
                    :placeholder="$t('profile.postalAddressPage.streetHint')"
                    :disabled="isUserKycLevel3"
                    required
                />
                <custom-input v-model="address.zipCode"
                    name="zipcode"
                    class="input--half"
                    :label="$t('profile.postalAddressPage.zipcode')"
                    :placeholder="$t('profile.postalAddressPage.zipcodeHint')"
                    :disabled="isUserKycLevel3"
                    required
                />
                <custom-input v-model="address.city"
                    name="city"
                    class="input--half"
                    :label="$t('profile.postalAddressPage.city')"
                    :placeholder="$t('profile.postalAddressPage.cityHint')"
                    :disabled="isUserKycLevel3"
                    required
                />
                <custom-input v-model="address.region"
                    name="region"
                    class="input--half"
                    :label="$t('kycLevel3PostalAddress.region')"
                    :placeholder="$t('profile.postalAddressPage.regionHint')"
                    :disabled="isUserKycLevel3"
                />

                <div class="label label--half label--bottom-padding">
                    {{ $t('profile.postalAddressPage.country') }}

                    <country-dropdown :country.sync="address.country"
                        :placeholder="$t('profile.postalAddressPage.countryHint')"
                        :noResultsMessage="$t('profile.postalAddressPage.noResultsCountries')"
                        :disabled="isCountryDisabled || isUserKycLevel3"
                        required
                    />
                </div>
            </section>

            <h2 class="section__title">{{ $t('turkishExtraFields.parentsInformations') }}</h2>
            <section class="section">
                <custom-input v-model="parent1FullName"
                    name="parent-1-full-name"
                    class="input--half"
                    :label="$t('turkishExtraFields.parent1Label')"
                    :placeholder="$t('turkishExtraFields.parentPlaceholder')"
                    required
                />
                <custom-input v-model="parent2FullName"
                    name="parent-2-full-name"
                    class="input--half"
                    :label="$t('turkishExtraFields.parent2Label')"
                    :placeholder="$t('turkishExtraFields.parentPlaceholder')"
                    required
                />
            </section>
        </div>
        <button class="submit-button"
            :disabled="!isFormValid"
            @click.prevent="submit"
        >
            {{ $t('global.button.submit') }}
        </button>

        <div class="support-note">
            <span class="support-note__question">{{ $t('contact.help') }}</span>
            <br>
            <span>
                {{ $t('contact.contact') }}
                <a class="support-note__link"
                    :href="contactUsUrl"
                    target="_blank"
                >
                    {{ $t('contact.customerSupport') }}
                </a>
            </span>
        </div>
    </form>
</template>

<script>
// 3rd party
import {
    mapActions,
    mapGetters,
} from 'vuex';
import socios from '@webapp/socios.json';

// Components
import countryDropdown from '@webapp/components/country-dropdown/country-dropdown.vue';
import customInput from '@webapp/components/form/custom-input.vue';

// Mixins
import postalAddressMixin from '@webapp/helpers/postal-address-mixin.js';

// Types
import Regulation from '@shared/types/regulation';

// Shared Modules
import sharedEpicTypes from '@shared/epics/types.js';

// Helpers
import {
    formatCountry,
} from '@webapp/helpers/country';

const REGULATION_IDS = {
    [Regulation.International]: 1,
    [Regulation.Usa]: 2,
};

export default {
    name: 'turkish-questionnaire',
    components: {
        countryDropdown,
        customInput,
    },
    mixins: [postalAddressMixin],
    data: () => ({
        cityOfBirth: '',
        countryOfBirth: null,
        parent1FullName: '',
        parent2FullName: '',
        isCountryDisabled: false,
    }),
    computed: {
        ...mapGetters([
            'localeWithUnderscore',
            'isUsaRegulation',
            'isKycTierLevelValidated',
        ]),

        isFormValid() {
            return Boolean(this.cityOfBirth) &&
                Boolean(this.countryOfBirth) &&
                Boolean(this.address.street) &&
                Boolean(this.address.zipCode) &&
                Boolean(this.address.city) &&
                Boolean(this.address.country) &&
                Boolean(this.parent1FullName) &&
                Boolean(this.parent2FullName);
        },

        contactUsUrl() {
            return `${socios.cmsUrl}/${this.localeWithUnderscore.toLowerCase()}/contact-us?regulation=${
                this.isUsaRegulation ? REGULATION_IDS[Regulation.Usa] : REGULATION_IDS[Regulation.International]
            }`;
        },

        isUserKycLevel3() {
            return this.isKycTierLevelValidated(3);
        },
    },
    watch: {
        userAddress: 'onInitUserAddress',
    },
    async mounted() {
        await this.getUserAddress();

        if (this.address.country) {
            this.isCountryDisabled = true;
        }
    },
    methods: {
        ...mapActions({
            getUserPermissions: sharedEpicTypes.GET_USER_PERMISSIONS,
        }),

        ...mapActions('turkishRequirements', ['submitTurkishExtraFieldsForm']),

        async submit() {
            if (!this.isFormValid) {
                return;
            }

            const form = {
                birthPlaceCity: this.cityOfBirth,
                birthPlaceCountry: this.countryOfBirth.iso2,
                streetAddress: this.address.street,
                zipCodeAddress: this.address.zipCode,
                countryCodeAddress: this.address.country.iso2,
                cityAddress: this.address.city,
                regionAddress: this.address.region || null,
                nameParentOne: this.parent1FullName,
                nameParentTwo: this.parent2FullName,
            };


            await this.submitTurkishExtraFieldsForm(form);
            await this.getUserPermissions();

            if (this.isKycTierLevelValidated(2)) {
                this.$router.go();
            }
        },
        onInitUserAddress(address) {
            this.address.street = address.street;
            this.address.city = address.city;
            this.address.region = address.region;
            this.address.zipCode = address.zipCode;
            this.address.country = formatCountry(address.countryCode);
        },
    },

};
</script>

<style lang="scss" scoped>
.form {
    display: flex;
    flex-direction: column;
    margin: 0 auto;
    padding: 1rem;
    max-width: 120rem;
    color: $daintree-color;
}

.input--half {
    width: 45%;

    @include max-width(lg) {
        width: 100%;
    }
}

.wrapper {
    margin-top: 5rem;
    padding: 2rem;
    background: #fff;
}

.header {
    font-size: 1.8rem;
}

.section {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem 5rem;

    &__title {
        font-size: 2rem;
    }
}

.submit-button {
    margin-top: 4rem;
    margin-left: auto;
    padding: 1.5rem 5rem;
    border-radius: 0.4rem;
    border: none;
    font-size: 1.5rem;
    background: $lima-color;
    cursor: pointer;

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
}

.support-note {
    font-size: 1.8rem;
    margin: 2rem;

    &__question {
        font-weight: bold;
    }

    &__link {
        font-weight: bold;
        color: #1872f2;
        text-decoration: none;
    }
}
</style>